import React, { forwardRef, useImperativeHandle, useState } from 'react';

import { InputStatus } from '../../../../common/helpers/getFinalFieldState.types';
import { defaultCountries } from '../../data/countryData';
import { usePhoneInput, UsePhoneInputConfig } from '../../hooks/usePhoneInput';
// buildClassNames plus SCSS have been replaced by styled-components in this module
import { ParsedCountry } from '../../types';
import {
  CountrySelector,
  CountrySelectorProps,
  CountrySelectorStyleProps,
} from '../CountrySelector/CountrySelector';
import {
  DialCodePreview,
  DialCodePreviewStyleProps,
} from '../DialCodePreview/DialCodePreview';

import { $Container, $Input } from './PhoneInput.styles';

export interface PhoneInputStyleProps {
  style?: React.CSSProperties;
  className?: string;

  inputStyle?: React.CSSProperties;
  inputClassName?: string;

  countrySelectorStyleProps?: CountrySelectorStyleProps;
  dialCodePreviewStyleProps?: DialCodePreviewStyleProps;
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export interface PhoneInputProps
  extends Omit<UsePhoneInputConfig, 'onChange'>, PhoneInputStyleProps {
  /**
   * @description Hide the dropdown icon. Make country selection not accessible.
   * @default false
   */
  hideDropdown?: CountrySelectorProps['hideDropdown'];

  /**
   * @description
   * Show prefix and dial code between country selector and phone input.
   * Works only when *disableDialCodeAndPrefix* is *true*
   * @default false
   */
  showDisabledDialCodeAndPrefix?: boolean;

  /**
   * @description Disable auto focus on input field after country select.
   * @default false
   */
  disableFocusAfterCountrySelect?: boolean;

  /**
   * @description Custom flag URLs array
   * @default undefined
   */
  flags?: CountrySelectorProps['flags'];

  /**
   * @description Callback that calls on phone change
   * @param phone - New phone value in E.164 format.
   * @param meta - Additional information about the phone.
   * @param data.country - New phone country object.
   * @param data.inputValue - Value that is displayed in input element.
   * @default undefined
   */
  onChange?: (
    phone: string,
    meta: {
      country: ParsedCountry;
      inputValue: string;
    },
  ) => void;

  /**
   * @description Default input component props
   * @default undefined
   */
  inputProps?: InputProps;

  // pass most used input props as top level props for easy integration
  onFocus?: InputProps['onFocus'];
  onBlur?: InputProps['onBlur'];
  name?: InputProps['name'];
  required?: InputProps['required'];
  autoFocus?: InputProps['autoFocus'];
  disabled?: InputProps['disabled'];
  placeholder?: InputProps['placeholder'];
  isSmall?: boolean;
  status?: InputStatus;
}

export type PhoneInputRefType =
  | null
  | (HTMLInputElement & {
      setCountry: ReturnType<typeof usePhoneInput>['setCountry'];
      state: {
        phone: string;
        inputValue: string;
        country: ParsedCountry;
      };
    });

export const PhoneInput = forwardRef<PhoneInputRefType, PhoneInputProps>(
  (
    {
      value,
      onChange,
      countries = defaultCountries,
      preferredCountries = [],
      hideDropdown,
      showDisabledDialCodeAndPrefix,
      disableFocusAfterCountrySelect,
      flags,

      style,
      className,
      inputStyle,
      inputClassName,
      countrySelectorStyleProps,
      dialCodePreviewStyleProps,

      inputProps,
      placeholder,
      disabled,
      name,
      onFocus,
      onBlur,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      required,
      autoFocus,
      isSmall,
      status,
      ...usePhoneInputConfig
    },
    ref,
  ) => {
    const {
      phone,
      inputValue,
      inputRef,
      country,
      setCountry,
      handlePhoneValueChange,
    } = usePhoneInput({
      value,
      countries,
      ...usePhoneInputConfig,
      onChange: (data) => {
        onChange?.(data.phone, {
          country: data.country,
          inputValue: data.inputValue,
        });
      },
    });

    const showDialCodePreview =
      usePhoneInputConfig.disableDialCodeAndPrefix &&
      showDisabledDialCodeAndPrefix &&
      country?.dialCode;

    useImperativeHandle<PhoneInputRefType, PhoneInputRefType>(ref, () => {
      if (!inputRef.current) {
        return null;
      }

      return Object.assign(inputRef.current, {
        // extend input ref with additional properties
        setCountry,
        state: {
          phone,
          inputValue,
          country,
        },
      });
    }, [inputRef, setCountry, phone, inputValue, country]);

    // Ensure CountrySelector button visually connects with the input
    const mergedCountrySelectorStyleProps: CountrySelectorStyleProps = {
      ...countrySelectorStyleProps,
      buttonStyle: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        marginRight: -1, // for border overlap
        ...(countrySelectorStyleProps?.buttonStyle ?? {}),
      },
    };

    const [isFocused, setIsFocused] = useState(false);

    return (
      <$Container
        ref={ref}
        className={className}
        style={style}
        $isFocused={isFocused}
        $isSmall={isSmall}
        $status={status}
      >
        <CountrySelector
          onSelect={(country) =>
            setCountry(country.iso2, {
              focusOnInput: !disableFocusAfterCountrySelect,
            })
          }
          flags={flags}
          selectedCountry={country.iso2}
          countries={countries}
          preferredCountries={preferredCountries}
          disabled={disabled}
          hideDropdown={hideDropdown}
          {...mergedCountrySelectorStyleProps}
        />

        {showDialCodePreview && (
          <DialCodePreview
            dialCode={country.dialCode}
            prefix={usePhoneInputConfig.prefix ?? '+'}
            disabled={disabled}
            {...dialCodePreviewStyleProps}
          />
        )}

        <$Input
          onChange={handlePhoneValueChange}
          value={inputValue}
          type="tel"
          ref={inputRef}
          className={inputClassName}
          placeholder={placeholder}
          disabled={disabled}
          style={inputStyle}
          name={name}
          autoFocus={autoFocus}
          onFocus={(e) => {
            setIsFocused(true);

            if (onFocus) {
              onFocus(e);
            }
          }}
          onBlur={(e) => {
            setIsFocused(false);

            if (onBlur) {
              onBlur(e);
            }
          }}
          {...inputProps}
        />
      </$Container>
    );
  },
);
